import { isValid } from 'date-fns';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

import { parseYearMonthDay } from '~/components/date/MonthRangePicker/MonthRangePicker';
import { exceedsMaxSpan } from '~/components/date/MonthRangePicker/periodRange';
import type { GlobalFiltersSelection } from '~/contexts/globalFilterContext';

type DateRange = GlobalFiltersSelection['date'];

/**
 * Reports the period the URL is asking for whenever it differs from the one selected.
 *
 * The global filter selection is seeded once from the app-shell loader and then only
 * ever written by the filter bar, but the URL's range is rewritten by things the filter
 * bar knows nothing about — chiefly the app shell's own default-range redirect, which
 * fires for any link that arrives without `start_date`/`end_date`. Nothing carried that
 * back, so the Period chip, the URL and the range sent to the API could all disagree,
 * and the URL was the one that was wrong: copying it showed someone else a different
 * view than the one on screen.
 *
 * One-way on purpose. Applying a filter writes the selection and the URL together, so
 * by the time the URL catches up the two already agree and this stays quiet. A route
 * that carries no range is not asking for one, and the reader's selection is not
 * that route's to reset.
 */
export function useUrlSyncedDateRange(
  selected: DateRange,
  onUrlDateRange: (range: DateRange) => void,
): void {
  const [searchParams] = useSearchParams();
  const start = searchParams.get('start_date');
  const end = searchParams.get('end_date');

  // The range last read out of the URL, so a re-render — or the caller applying what it
  // was just handed, which changes `selected` — doesn't report the same one twice.
  const lastReadFromUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!start || !end) {
      lastReadFromUrl.current = null;
      return;
    }

    const range = `${start}:${end}`;
    if (lastReadFromUrl.current === range) return;
    lastReadFromUrl.current = range;

    // Validated before the selection is read, not after.
    //
    // `ensureDateRangeInParamsOrRedirect` now rejects an unparseable date at the loader
    // and redirects to the default range, so this branch should not be reachable from a
    // URL any more. It stays because returning early here is not enough on its own: an
    // `Invalid Date` that did reach global filter state would be formatted during
    // render by the Period chip and the contract KPI SWR key, and only correcting the
    // URL keeps it out. A backwards range is dropped for a quieter reason: it reaches
    // the API as an inverted window and comes back empty, which reads as "no data"
    // rather than as a bad URL.
    const parsedStart = parseYearMonthDay(start);
    const parsedEnd = parseYearMonthDay(end);
    if (!isValid(parsedStart) || !isValid(parsedEnd) || parsedStart > parsedEnd) return;
    // Longer than the API accepts: the loader clamps these, so this is the same
    // belt-and-braces as the checks above, keeping such a range out of the selection.
    if (exceedsMaxSpan(parsedStart, parsedEnd)) return;

    // Compared on timestamps rather than by formatting the selection. The app shell
    // seeds the selection from these same params, so a direct load carrying an
    // unparseable date leaves an `Invalid Date` in it, and `dateToDateString` is
    // `date-fns` `format`, which throws on one. `getTime()` gives `NaN` there instead,
    // which matches nothing, so the invalid selection is replaced by the valid URL range.
    if (
      selected.start.getTime() === parsedStart.getTime() &&
      selected.end.getTime() === parsedEnd.getTime()
    ) {
      return;
    }

    onUrlDateRange({ start: parsedStart, end: parsedEnd });
  }, [start, end, selected, onUrlDateRange]);
}
